import { useState, useEffect } from "react";
import { Link } from "wouter";
import PageMetadata from "@/components/PageMetadata";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import BottomCTA from "@/components/BottomCTA";
import allocationWorkflow from "@assets/01b-allocation-workflow_1785715424448.png";
import {
  Database,
  Zap,
  BarChart2,
  GitMerge,
  ArrowRightLeft,
  RefreshCw,
  Bot,
  TrendingUp,
  CheckCircle2,
  Clock,
  ShieldCheck,
} from "lucide-react";

const tocItems = [
  { id: "situation", label: "The Situation", icon: BarChart2 },
  { id: "the-build", label: "What Brandlark Built", icon: GitMerge },
  { id: "data-collection", label: "Step 1: Automated Data Collection", icon: Database },
  { id: "gce-scoring", label: "Step 2: Continuous GCE Scoring", icon: Zap },
  { id: "recommendations", label: "Step 3: One-Click Recommendations", icon: CheckCircle2 },
  { id: "reallocation", label: "Step 4: Channel Reallocation", icon: ArrowRightLeft },
  { id: "handoff", label: "The Handoff", icon: ShieldCheck },
  { id: "performance-loop", label: "The Performance Loop", icon: RefreshCw },
  { id: "future-state", label: "The Future State: Agentic AI", icon: Bot },
  { id: "results", label: "Results", icon: TrendingUp },
];

export default function GrowthCapitalAllocationCaseStudy() {
  const [activeId, setActiveId] = useState("situation");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          const topmost = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveId(topmost.target.id);
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );
    tocItems.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageMetadata path="/case-studies/growth-capital-allocation-system" />
      <Header />
      <article className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_300px] gap-12">

          {/* ── MAIN CONTENT ── */}
          <div className="max-w-4xl">

            {/* Article header */}
            <div className="mb-12 rounded-2xl bg-[linear-gradient(160deg,#12101F_0%,#1E1B38_52%,#2C2159_100%)] p-8 md:p-12">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="bg-[rgba(124,92,246,0.16)] border border-[rgba(124,92,246,0.35)] text-[#C4B5FD] rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest">Case Study</span>
                <span className="text-white/[0.35]">·</span>
                <span className="text-xs text-white/[0.68]">Kelso Outdoor Goods · Fashion &amp; Apparel</span>
                <span className="text-white/[0.35]">·</span>
                <span className="text-xs text-white/[0.68]">Published April 2026</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6 leading-tight text-white">Building a Growth Capital Efficiency System: How Kelso Outdoor Goods Turned Budget Decisions into Automated, Repeatable Revenue Growth</h1>
              <p className="text-xl text-white/[0.68] leading-relaxed">
                Brandlark designed and built a fully automated system that decides where the growth budget goes for a $12M/year outdoor apparel brand, then handed it off. The client's only ongoing responsibility: approve or defer a weekly recommendation. Everything else runs itself.
              </p>
            </div>

            {/* Key outcome strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 p-6 bg-card border rounded-xl transition-all duration-200 hover:shadow-xl hover:border-[#E3E0FB] hover:-translate-y-1">
              {[
                { label: "12-Month Net Lift", value: "$420K" },
                { label: "GCE Improvement", value: "4.2x → 6.9x" },
                { label: "Steady-State Monthly Lift", value: "$37K/mo" },
                { label: "Client Time Per Week", value: "<30 min" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-[#4F46E5] mb-1">{stat.value}</div>
                  <div className="text-xs text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* ── SECTION 1: SITUATION ── */}
            <section id="situation" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">The Situation</h2>
              <p className="text-lg leading-relaxed mb-5">
                Kelso Outdoor Goods is a Nashville-based apparel and outdoor gear brand generating $12M in annual revenue. By the time they approached Brandlark, they had an $52K/month marketing budget spread across five channels — Meta, Google Search, retargeting, email/SMS, and SEO — but no consistent way to know which spend was working.
              </p>
              <p className="text-lg leading-relaxed mb-5">
                Blended ROAS had declined for three consecutive quarters. Every budget decision required a manual pull from three different platform dashboards, and those numbers never agreed with each other. The team was spending real time debating channel allocation with no reliable framework to resolve the disagreement.
              </p>
              <div className="bg-card border rounded-xl p-7 mb-5">
                <h3 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-5">Where Kelso stood at engagement start</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { label: "Annual Revenue", value: "$12M", sub: "Outdoor apparel, direct-to-consumer" },
                    { label: "Monthly Marketing Budget", value: "$52K", sub: "Across 5 active channels" },
                    { label: "Blended ROAS Trend", value: "↓ 3 qtrs", sub: "No clear root cause identified" },
                  ].map((item, i) => (
                    <div key={i} className="text-center p-4 bg-muted/40 rounded-lg">
                      <div className="text-xl font-bold mb-1">{item.value}</div>
                      <div className="text-xs font-semibold text-[#4F46E5] mb-1">{item.label}</div>
                      <div className="text-xs text-muted-foreground">{item.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-lg leading-relaxed">
                The core problem wasn't spend level — it was allocation discipline. Kelso had no systematic way to compare initiatives across channels, no scoring framework to decide which opportunities got funded, and no automated monitoring to catch underperformance before it became expensive.
              </p>
            </section>

            {/* ── SECTION 2: THE BUILD ── */}
            <section id="the-build" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">What Brandlark Built</h2>
              <p className="text-lg leading-relaxed mb-6">
                Brandlark's role was to design, build, and validate the system — then hand it off. The engagement was scoped as a systems architecture project, not an ongoing management retainer. Kelso would own and operate everything after delivery.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                Over a 60-day build period, Brandlark assembled four interconnected components:
              </p>

              <div className="space-y-4 mb-8">
                {[
                  {
                    n: "01",
                    title: "Data connectors",
                    desc: "Integrations to Shopify, Meta Ads, Google Ads, Klaviyo, and GA4 that automatically pull unit economics, channel performance, and attribution data on a daily refresh cycle. No manual exports. No dashboard maintenance.",
                  },
                  {
                    n: "02",
                    title: "GCE scoring engine",
                    desc: "A continuously running model that re-scores all candidate growth initiatives weekly against the live portfolio GCE baseline. When a channel's performance shifts, the scores update automatically — no analyst required to rerun the numbers.",
                  },
                  {
                    n: "03",
                    title: "Weekly recommendation layer",
                    desc: "A plain-language output that tells Kelso's team exactly what to approve, what to defer, and why — expressed in dollars and expected revenue impact, not marketing jargon. The most complex action required of any team member is a single click.",
                  },
                  {
                    n: "04",
                    title: "Performance dashboard",
                    desc: "A single view showing current allocation versus GCE-weighted recommended allocation, initiative status, and trailing performance against the revenue baseline. No new tools to learn — the dashboard surfaces only what's needed to make the approval decision.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5 bg-card border rounded-xl p-6">
                    <div className="w-9 h-9 rounded-lg bg-[rgba(124,92,246,0.12)] border border-[rgba(124,92,246,0.28)] flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-[#4F46E5]">{item.n}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-2">{item.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <figure className="overflow-hidden rounded-xl border border-border shadow-sm mb-8">
                <img
                  src={allocationWorkflow}
                  alt="Growth Capital Allocation System workflow diagram"
                  className="w-full h-auto block"
                  width={1600}
                  height={1200}
                  loading="lazy"
                />
                <figcaption className="px-5 py-4 bg-card text-xs text-muted-foreground">
                  The weekly allocation loop: pull data, score initiatives, approve the recommendation, and measure the result.
                </figcaption>
              </figure>

              <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
                <p className="text-sm leading-relaxed text-foreground/80">
                  <strong>The design constraint Brandlark enforced:</strong> every component had to be operable by someone with no technical background. The system was built to run itself. Kelso's team needed to understand what they were approving — not how the system produced the recommendation.
                </p>
              </div>
            </section>

            {/* ── SECTION 3: DATA COLLECTION ── */}
            <section id="data-collection" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">Step 1: Automated Data Collection</h2>
              <p className="text-lg leading-relaxed mb-5">
                In a manual diagnostic, a team member would log into each platform, export performance data, reconcile the numbers, and calculate unit economics by hand. That process takes hours and introduces reconciliation errors every time it runs.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                In Kelso's system, this happens automatically. Each platform connector pulls its designated data on a daily schedule, feeds it into the GCE engine, and updates the scoring model without any human input. Unit economics are computed from live data — not entered manually and not estimated.
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full border border-border rounded-xl overflow-hidden text-sm">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Platform</th>
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Data Pulled Automatically</th>
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Used to Calculate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { platform: "Shopify", data: "Orders, revenue, returns, SKU-level margin", calc: "AOV, GPMR, monthly order volume, CLV inputs" },
                      { platform: "Meta Ads", data: "Spend by campaign, attributed revenue, clicks", calc: "CAC (paid social), channel ROAS, cost per acquisition" },
                      { platform: "Google Ads", data: "Spend by campaign type, conversions, search impression share", calc: "CAC (paid search), brand vs. non-brand ratio" },
                      { platform: "Klaviyo", data: "Email/SMS attributed revenue, list growth, flows performance", calc: "Email revenue share, lifecycle contribution" },
                      { platform: "GA4", data: "Sessions by source, conversion rate by channel, add-to-cart rate", calc: "CVR, funnel drop-off points, mobile vs. desktop gap" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-5 py-4 font-semibold text-sm">{row.platform}</td>
                        <td className="px-5 py-4 text-muted-foreground text-sm">{row.data}</td>
                        <td className="px-5 py-4 text-muted-foreground text-sm">{row.calc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-base leading-relaxed mb-6 text-muted-foreground">
                Once the connectors were live, Kelso's unit economics populated automatically within 48 hours:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {[
                  { metric: "AOV", value: "$94", source: "Shopify" },
                  { metric: "Gross Product Margin", value: "52%", source: "Shopify + cost data" },
                  { metric: "Customer Acquisition Cost", value: "$41", source: "Meta + Google Ads" },
                  { metric: "Sitewide Conversion Rate", value: "2.1%", source: "GA4" },
                  { metric: "Customer Lifetime Value", value: "$211", source: "Derived" },
                  { metric: "CLV:CAC Ratio", value: "5.1:1", source: "Derived" },
                ].map((item, i) => (
                  <div key={i} className="p-4 bg-card border rounded-lg transition-all duration-200 hover:shadow-xl hover:border-[#E3E0FB] hover:-translate-y-1">
                    <div className="text-xl font-bold text-[#4F46E5] mb-1">{item.value}</div>
                    <div className="text-xs font-semibold mb-0.5">{item.metric}</div>
                    <div className="text-xs text-muted-foreground">Source: {item.source}</div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                These numbers feed the GCE engine in real time. When Kelso's Shopify margin changes after a product line adjustment, the scores update automatically on the next daily refresh.
              </p>
            </section>

            {/* ── SECTION 4: GCE SCORING ── */}
            <section id="gce-scoring" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">Step 2: Continuous GCE Scoring</h2>
              <p className="text-lg leading-relaxed mb-5">
                The GCE engine runs every week against Kelso's live portfolio data. It scores all candidate growth initiatives using the Growth Capital Efficiency formula — ROAS × Gross Product Margin — and ranks them against the portfolio baseline.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                The baseline (4.2x for Kelso at engagement start) is the median GCE score across all currently active initiatives. Any initiative scoring above the baseline is eligible for funding. Anything below is deferred until conditions change — or until quarterly recalibration moves the baseline.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border border-border rounded-xl overflow-hidden text-sm">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Initiative</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">GCE Score</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Capital Required</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Time to Revenue</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: "Email Lifecycle Automation", gce: "7.6x", capital: "$4,200", ttc: "3 weeks", funded: true },
                      { name: "Cart Abandonment Recovery", gce: "6.8x", capital: "$6,000", ttc: "2 weeks", funded: true },
                      { name: "PDP Optimization (Mobile)", gce: "5.1x", capital: "$10,200", ttc: "4 weeks", funded: true },
                      { name: "Retargeting Rebuild", gce: "4.5x", capital: "$4,000", ttc: "2 weeks", funded: true },
                      { name: "SEO Content Sprint", gce: "3.8x", capital: "$8,000", ttc: "14 weeks", funded: false },
                      { name: "Referral Program Launch", gce: "3.1x", capital: "$8,200", ttc: "8 weeks", funded: false },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-border last:border-0 ${row.funded ? "bg-[rgba(124,92,246,0.08)]" : ""}`}>
                        <td className="px-5 py-4 font-medium text-sm">{row.name}</td>
                        <td className={`px-5 py-4 text-right font-bold ${row.funded ? "text-[#4F46E5]" : "text-muted-foreground"}`}>{row.gce}</td>
                        <td className="px-5 py-4 text-right text-muted-foreground text-sm">{row.capital}</td>
                        <td className="px-5 py-4 text-right text-muted-foreground text-sm">{row.ttc}</td>
                        <td className="px-5 py-4 text-right">
                          {row.funded ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#4F46E5] bg-[rgba(124,92,246,0.12)] px-2.5 py-1 rounded-full">
                              Funded
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-full">
                              Deferred
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-card border rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <div className="w-1 flex-shrink-0 bg-[#4F46E5] rounded-full self-stretch" />
                  <div>
                    <p className="text-sm font-semibold mb-1">Baseline: 4.2x (portfolio median at engagement start)</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      All four initiatives scoring above 4.2x were funded. SEO and Referral scored below the baseline — not because they are bad ideas, but because at Kelso's current unit economics, the capital generates better return elsewhere. Both are reviewed at every quarterly recalibration.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-base leading-relaxed mt-5 text-muted-foreground">
                No analyst manually scores these. When Kelso's AOV shifts after a product launch, or when a new initiative is proposed, the engine re-scores the full list on the next weekly cycle. The output is always current.
              </p>
            </section>

            {/* ── SECTION 5: ONE-CLICK RECOMMENDATIONS ── */}
            <section id="recommendations" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">Step 3: One-Click Recommendations</h2>
              <p className="text-lg leading-relaxed mb-5">
                Each week, the system surfaces a single recommendation package to Kelso's team. The package is written in plain language — what to change, how much to move, and what the expected revenue impact is. There is no marketing jargon, no dashboard to interpret, and no calculation required.
              </p>

              <div className="bg-card border rounded-xl p-7 mb-8">
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-5">Example: Kelso's first weekly recommendation</div>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-muted/40 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                    <div>
                      <div className="text-sm font-semibold mb-1">Reduce: Meta Branded Campaigns</div>
                      <div className="text-sm text-muted-foreground">Cut $3,800/mo. These campaigns are capturing demand that would arrive organically — GCE score 1.9x, below baseline. Recovered capital funds the initiatives below.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-[rgba(124,92,246,0.08)] rounded-lg border border-[rgba(124,92,246,0.22)]">
                    <div className="w-2 h-2 rounded-full bg-[#4F46E5] flex-shrink-0 mt-2" />
                    <div>
                      <div className="text-sm font-semibold mb-1">Fund: Retargeting Rebuild — +$2,200/mo</div>
                      <div className="text-sm text-muted-foreground">Your retargeting audience pools are undersized relative to traffic volume. Estimated incremental revenue: $8,400/mo at steady state. GCE: 4.5x.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-[rgba(124,92,246,0.08)] rounded-lg border border-[rgba(124,92,246,0.22)]">
                    <div className="w-2 h-2 rounded-full bg-[#4F46E5] flex-shrink-0 mt-2" />
                    <div>
                      <div className="text-sm font-semibold mb-1">Fund: Email Lifecycle Infrastructure — +$1,600/mo</div>
                      <div className="text-sm text-muted-foreground">Klaviyo flows for post-purchase and winback. Estimated incremental revenue: $11,200/mo. GCE: 7.6x — highest-scoring initiative in the current portfolio.</div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t flex items-center justify-between flex-wrap gap-3">
                  <div className="text-sm text-muted-foreground">Total budget change: <strong className="text-foreground">$0</strong> — fully funded from recovered branded spend</div>
                  <div className="flex gap-3">
                    <button className="text-sm font-semibold rounded-full bg-[linear-gradient(90deg,#4F46E5,#7C5CF6)] text-white px-4 py-2 shadow-[0_6px_20px_rgba(79,70,229,0.3)]">Approve all</button>
                    <button className="text-sm font-medium border px-4 py-2 rounded-lg text-muted-foreground">Defer</button>
                  </div>
                </div>
              </div>

              <p className="text-lg leading-relaxed mb-4">
                Total initiative budget: $24.4K — drawn entirely from waste recovered in existing spend. Kelso did not increase their total marketing budget by a single dollar.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                The system modeled the GCE impact of each shift before generating the recommendation. Kelso's Head of Growth approved the first package without needing to understand the underlying math — only the business outcome.
              </p>
            </section>

            {/* ── SECTION 6: CHANNEL REALLOCATION ── */}
            <section id="reallocation" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">Step 4: Channel Reallocation</h2>
              <p className="text-lg leading-relaxed mb-8">
                Over the first 60 days of system operation, Kelso's channel mix shifted substantially — entirely through the weekly approval process. No budget review meetings. No agency presentations. Just a sequence of one-click approvals that the system translated into execution directives.
              </p>

              <div className="overflow-x-auto mb-8">
                <table className="w-full border border-border rounded-xl overflow-hidden text-sm">
                  <thead>
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Channel</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Before</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">After</th>
                      <th className="text-right px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Change</th>
                      <th className="text-left px-5 py-3 font-semibold text-muted-foreground text-xs uppercase tracking-widest">Rationale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { channel: "Meta Branded", before: "32%", after: "18%", change: "−14%", dir: "down", rationale: "Branded ads taking credit for sales organic search would capture anyway" },
                      { channel: "Google Search", before: "28%", after: "28%", change: "—", dir: "flat", rationale: "Non-brand performance within acceptable range" },
                      { channel: "Retargeting", before: "12%", after: "22%", change: "+10%", dir: "up", rationale: "Underutilized pool relative to traffic volume" },
                      { channel: "Email / SMS", before: "8%", after: "18%", change: "+10%", dir: "up", rationale: "Highest GCE in portfolio at 7.6x — underallocated" },
                      { channel: "SEO / Content", before: "20%", after: "14%", change: "−6%", dir: "down", rationale: "Deferred below baseline — revisit at Q3 recalibration" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-5 py-4 font-medium text-sm">{row.channel}</td>
                        <td className="px-5 py-4 text-right text-muted-foreground">{row.before}</td>
                        <td className="px-5 py-4 text-right font-semibold">{row.after}</td>
                        <td className={`px-5 py-4 text-right font-bold ${row.dir === "up" ? "text-[#4F46E5]" : row.dir === "down" ? "text-red-500" : "text-muted-foreground"}`}>
                          {row.change}
                        </td>
                        <td className="px-5 py-4 text-muted-foreground text-xs">{row.rationale}</td>
                      </tr>
                    ))}
                    <tr className="bg-muted/30 border-t-2 border-border">
                      <td className="px-5 py-3 font-bold text-sm">Total budget</td>
                      <td className="px-5 py-3 text-right font-bold">100%</td>
                      <td className="px-5 py-3 text-right font-bold">100%</td>
                      <td className="px-5 py-3 text-right font-bold text-muted-foreground">$0 net change</td>
                      <td className="px-5 py-3 text-xs text-muted-foreground">Same total spend, different allocation</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-base leading-relaxed text-muted-foreground">
                The system modeled the expected GCE impact of each proposed shift before surfacing it as a recommendation. Kelso reviewed the business outcome — not the model — and approved or deferred. The execution directives were generated automatically.
              </p>
            </section>

            {/* ── SECTION 7: THE HANDOFF ── */}
            <section id="handoff" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">The Handoff</h2>
              <p className="text-lg leading-relaxed mb-5">
                After 60 days of build and validation, Brandlark handed the system off to Kelso. The handoff was structured around one principle: the client should be able to run this without Brandlark in the room.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                That meant the handoff wasn't just a credentials transfer. It was a deliberate knowledge transfer — making sure Kelso's team understood what the system was doing, why it was making each recommendation, and what to do if something looked wrong.
              </p>

              <div className="bg-card border rounded-xl divide-y divide-border mb-8">
                <div className="p-6">
                  <h3 className="font-semibold text-sm uppercase tracking-widest text-muted-foreground mb-4">What the handoff included</h3>
                </div>
                {[
                  {
                    icon: ShieldCheck,
                    title: "One-page operating guide",
                    desc: "A plain-language document covering three scenarios: what to approve automatically, what to defer for a follow-up discussion, and when to escalate to Brandlark's quarterly review. No technical background required to use it.",
                  },
                  {
                    icon: Clock,
                    title: "Recorded walkthrough",
                    desc: "A screen-recorded walkthrough of the full weekly recommendation flow — from data refresh to approval — so any team member could onboard without a call. Kelso saved this in their internal Notion alongside the operating guide.",
                  },
                  {
                    icon: RefreshCw,
                    title: "Quarterly recalibration cadence",
                    desc: "A scheduled quarterly check-in with Brandlark to recalibrate the GCE baseline, refresh the initiative pipeline with new candidates, and review any structural changes to Kelso's business that should update the model.",
                  },
                  {
                    icon: Database,
                    title: "Connector maintenance protocol",
                    desc: "Simple instructions for what to do if a platform integration breaks (usually an API key rotation). Kelso handles this in under 10 minutes with a documented one-step process. No developer required.",
                  },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="p-6 flex items-start gap-4">
                      <Icon className="w-5 h-5 text-[#4F46E5] flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold mb-1 text-sm">{item.title}</div>
                        <div className="text-sm text-muted-foreground leading-relaxed">{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-gradient-to-r from-[#7C5CF6]/10 to-[#4F46E5]/10 border border-[rgba(124,92,246,0.28)] rounded-xl p-7">
                <p className="text-base leading-relaxed">
                  <strong>The design intent:</strong> the most complex action a Kelso team member ever takes is clicking "Approve" or "Defer." If understanding the recommendation requires more than reading two sentences, the system failed its design requirement. Brandlark enforced this standard throughout the build.
                </p>
              </div>
            </section>

            {/* ── SECTION 8: PERFORMANCE LOOP ── */}
            <section id="performance-loop" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">The Performance Loop</h2>
              <p className="text-lg leading-relaxed mb-8">
                Post-handoff, the system maintains itself through three overlapping review cycles. Kelso's internal team time across all three: under 30 minutes per week.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  {
                    freq: "Weekly",
                    color: "text-[#4F46E5]",
                    bg: "bg-[rgba(124,92,246,0.08)] border-[rgba(124,92,246,0.28)]",
                    title: "Recommendation + threshold monitoring",
                    items: [
                      "System surfaces one recommendation package (approve or defer)",
                      "Threshold breach alerts fire automatically if any channel drops below predefined ROAS floor",
                      "No meeting required — review takes 10–15 minutes",
                    ],
                  },
                  {
                    freq: "Monthly",
                    color: "text-[#4F46E5]",
                    bg: "bg-[rgba(124,92,246,0.08)] border-[rgba(124,92,246,0.28)]",
                    title: "Trailing performance review",
                    items: [
                      "System generates a trailing 30-day performance summary against GCE projections",
                      "Any initiative more than 15% off projection triggers an automatic variance flag",
                      "Kelso reviews and approves any proposed adjustments — no manual analysis required",
                    ],
                  },
                  {
                    freq: "Quarterly",
                    color: "text-purple-500",
                    bg: "bg-purple-500/5 border-purple-500/20",
                    title: "Baseline recalibration (Brandlark-assisted)",
                    items: [
                      "Full portfolio re-score against updated GCE baseline",
                      "Initiative pipeline refreshed with new candidates based on current business state",
                      "Structural changes (new product lines, market shifts) incorporated into the model",
                      "60-minute call with Brandlark — the only scheduled external touchpoint",
                    ],
                  },
                ].map((cycle, i) => (
                  <div key={i} className={`border rounded-xl p-6 ${cycle.bg}`}>
                    <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${cycle.color}`}>{cycle.freq}</div>
                    <div className="font-semibold mb-3 text-sm">{cycle.title}</div>
                    <ul className="space-y-1.5">
                      {cycle.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className={`mt-1 flex-shrink-0 ${cycle.color}`}>·</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <p className="text-base leading-relaxed text-muted-foreground">
                The quarterly call is the only scheduled external dependency in Kelso's system. Everything else — monitoring, scoring, flagging, recommendation generation — runs without a human in the loop until a decision is required.
              </p>
            </section>

            {/* ── SECTION 9: FUTURE STATE ── */}
            <section id="future-state" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">The Future State: Agentic AI</h2>
              <p className="text-lg leading-relaxed mb-5">
                Kelso's system today operates on a human-in-the-loop approval model: the engine scores, surfaces recommendations, and executes what's approved. That model works — and it's deliberately conservative. Client approval remains the authority for any capital decision.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                The next evolution of the system architecture introduces agentic AI layers that expand what the system can do autonomously, while preserving the same approval-based ownership model.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  {
                    title: "Self-expanding integrations",
                    desc: "Agentic AI that can identify a new relevant platform (TikTok Ads, Amazon DSP, a new loyalty tool) and build the data connector autonomously — presenting the integration to the client for approval before activating it. No developer engagement required.",
                  },
                  {
                    title: "Autonomous initiative generation",
                    desc: "Rather than scoring a fixed pipeline of candidate initiatives, the agent continuously scans for new opportunities — emerging search demand patterns, competitive spend shifts, audience behavior changes — and proposes net-new initiatives for human review.",
                  },
                  {
                    title: "Self-calibrating baselines",
                    desc: "Instead of quarterly Brandlark-assisted recalibrations, the model recalibrates its own GCE baseline continuously as the portfolio evolves. Quarterly calls shift from recalibration to strategy — reviewing what the model proposed versus what actually happened.",
                  },
                  {
                    title: "Variance-triggered root cause analysis",
                    desc: "When a channel underperforms against projection, the agent investigates the data automatically — isolating whether the cause is seasonal, competitive, creative fatigue, or structural — and surfaces a root-cause hypothesis alongside the variance flag.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5 bg-card border rounded-xl p-6">
                    <Bot className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold mb-2 text-sm">{item.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-card border rounded-xl p-6">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  <strong className="text-foreground">The ownership model doesn't change.</strong> As the system gets smarter, the client remains the approval authority for every capital decision. Agentic AI expands what the system can surface — it doesn't replace the human judgment that decides what gets executed.
                </p>
              </div>
            </section>

            {/* ── SECTION 10: RESULTS ── */}
            <section id="results" className="mb-14">
              <h2 className="font-display text-3xl font-bold mb-6">Results</h2>
              <p className="text-lg leading-relaxed mb-8">
                Twelve months after system delivery, Kelso's numbers told a clear story. Not just in revenue — in how the business approached marketing capital entirely.
              </p>

              <div className="grid md:grid-cols-2 gap-5 mb-10">
                {[
                  { label: "12-Month Net Revenue Lift", value: "$420K", sub: "Above prior-year same-period baseline, verified through Shopify and platform data" },
                  { label: "Steady-State Monthly Increment", value: "$37K/mo", sub: "At full run rate with all four funded initiatives live" },
                  { label: "Portfolio GCE Improvement", value: "4.2x → 6.9x", sub: "Median GCE score across all active initiatives" },
                  { label: "Phase 1 Lift (Months 1–2)", value: "$22K/mo", sub: "Email automation and cart recovery live within first 6 weeks" },
                  { label: "Total Initiative Investment", value: "$24.4K", sub: "Funded entirely from recovered branded spend — zero net budget increase" },
                  { label: "Client Time Per Week", value: "<30 min", sub: "Weekly approval review — the only required human action post-handoff" },
                ].map((item, i) => (
                  <div key={i} className="bg-card border rounded-xl p-6 transition-all duration-200 hover:shadow-xl hover:border-[#E3E0FB] hover:-translate-y-1">
                    <div className="text-2xl font-bold text-[#4F46E5] mb-1">{item.value}</div>
                    <div className="text-sm font-semibold mb-2">{item.label}</div>
                    <div className="text-xs text-muted-foreground leading-relaxed">{item.sub}</div>
                  </div>
                ))}
              </div>

              {/* Testimonial */}
              <div className="bg-muted/40 rounded-xl p-8 border">
                <div className="flex gap-1 mb-5">
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                  ))}
                </div>
                <blockquote className="text-lg leading-relaxed mb-5 italic">
                  "Before this system, every budget decision was a negotiation. Someone would pull numbers from Meta, someone else would pull from Google, and we'd spend an hour arguing about which source was right. Now the system tells us what to approve, we click once, and we move on. I don't think I've spent more than 20 minutes a week on marketing budget decisions since we launched it. The $420K lift is real — but honestly, the time we got back is what surprised me most."
                </blockquote>
                <div className="text-sm">
                  <div className="font-semibold">Marcus Thill</div>
                  <div className="text-muted-foreground">Head of Growth, Kelso Outdoor Goods</div>
                </div>
              </div>
            </section>

            {/* Back link */}
            <div className="mt-12 pt-8 border-t flex items-center justify-between flex-wrap gap-4">
              <Link href="/case-studies">
                <span className="text-[#4F46E5] hover:text-[#3730A3] font-semibold cursor-pointer text-sm">
                  ← Back to Case Studies
                </span>
              </Link>
              <Link href="/scorecard">
                <span className="text-sm font-semibold text-[#4F46E5] hover:text-[#3730A3] cursor-pointer">
                  See your GCE score →
                </span>
              </Link>
            </div>
          </div>

          {/* ── SIDEBAR ── */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-4">
              <Card className="p-6">
                <h3 className="font-semibold mb-4 text-sm uppercase tracking-wide text-muted-foreground">
                  In this case study
                </h3>
                <nav>
                  <ul className="space-y-2.5">
                    {tocItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeId === item.id;
                      return (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            className={`flex items-start gap-3 text-sm transition-colors group ${isActive ? "text-primary" : "hover:text-primary"}`}
                          >
                            <Icon className={`h-3.5 w-3.5 mt-0.5 transition-colors flex-shrink-0 ${isActive ? "text-primary" : "text-muted-foreground group-hover:text-primary"}`} />
                            <span className={`leading-tight ${isActive ? "font-semibold" : ""}`}>{item.label}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </Card>

              <Card className="p-6">
                <h3 className="font-semibold mb-1 text-sm">Client overview</h3>
                <div className="space-y-2 mt-3">
                  {[
                    { label: "Client", value: "Kelso Outdoor Goods" },
                    { label: "Industry", value: "Fashion & Apparel" },
                    { label: "Revenue", value: "$12M/year" },
                    { label: "Build period", value: "60 days" },
                    { label: "Engagement type", value: "System build + handoff" },
                  ].map((row, i) => (
                    <div key={i} className="flex justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className="font-medium text-right">{row.value}</span>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-6 bg-[rgba(124,92,246,0.08)] border-[rgba(124,92,246,0.28)] transition-all duration-200 hover:shadow-xl hover:border-[#E3E0FB] hover:-translate-y-1">
                <h3 className="font-semibold mb-2 text-sm">See how your capital is allocated</h3>
                <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                  The GCE Scorecard runs the same scoring engine on your business — in about 6 minutes.
                </p>
                <Link href="/scorecard">
                  <span className="text-xs font-semibold text-[#4F46E5] hover:text-[#3730A3] cursor-pointer">
                    Take the Scorecard →
                  </span>
                </Link>
              </Card>
            </div>
          </aside>
        </div>
      </article>
      <BottomCTA
        heading="Want a System Like This Built for Your Business?"
        description="The Growth Blueprint is the starting point — a fixed-fee diagnostic that maps your highest-GCE opportunities before any system is built."
        primaryButtonText="Request a Growth Blueprint"
        primaryButtonHref="/growth-blueprint"
        headingTestId="heading-cta"
        primaryButtonTestId="link-get-growth-plan"
      />
      <Footer />
    </div>
  );
}
